/**
 * The irregular outline of a lesson's glow: a blob, never a circle.
 *
 * The radius at angle θ is a base radius swelled and dented by three gentle
 * waves (2, 3 and 5 lobes round the shape), each with an amplitude and phase
 * drawn from a hash of the lesson's id. The result is an organic, lumpy
 * outline that is different for every lesson and identical on every visit.
 *
 * Bounded: the waves never take the radius outside `[1 - SPREAD, 1 + SPREAD]`
 * times the base, so a blob stays inside the room the layout reserved and
 * still reads as one body rather than a starfish.
 */

/** How far the outline may swell or dent, as a share of the base radius. */
export const BLOB_SPREAD = 0.3;

const LOBES = [2, 3, 5] as const;

/** A stable 0..1 from a string and a salt. */
export function hash01(id: string, salt: number): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

/** The waves for one lesson: amplitude (summing to SPREAD) and phase. */
function waves(seed: string) {
  const raw = LOBES.map((_, k) => 0.4 + hash01(seed, 11 + k));
  const total = raw.reduce((a, b) => a + b, 0);
  return LOBES.map((lobes, k) => ({
    lobes,
    amplitude: (raw[k] / total) * BLOB_SPREAD,
    phase: hash01(seed, 101 + k) * Math.PI * 2,
  }));
}

/** The blob's radius at angle `theta` (radians), for a lesson `seed`. */
export function blobRadius(seed: string, base: number, theta: number): number {
  let f = 1;
  for (const w of waves(seed))
    f += w.amplitude * Math.sin(w.lobes * theta + w.phase);
  return base * f;
}

/** The outline as points, `steps` of them round the blob. */
export function blobOutline(
  seed: string,
  cx: number,
  cy: number,
  base: number,
  steps = 72
): Array<{ x: number; y: number }> {
  return Array.from({ length: steps }, (_, i) => {
    const theta = (i / steps) * Math.PI * 2;
    const r = blobRadius(seed, base, theta);
    return { x: cx + r * Math.cos(theta), y: cy + r * Math.sin(theta) };
  });
}
